import {hashKey as defaultHashKey, QueryClient, type QueryKey} from '@tanstack/react-query'

/**
 * Converts URLSearchParams to a sorted query string.
 */
function serializeURLSearchParams(params: URLSearchParams): string {
  return [...params.entries()]
    .sort(([aKey], [bKey]) => aKey.localeCompare(bKey))
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&')
}
/**
 * Recursively normalize query keys to serialize URLSearchParams to strings.
 */
function normalizeForHashing(value: unknown): unknown {
  if (value instanceof URLSearchParams) {
    return serializeURLSearchParams(value)
  }

  if (Array.isArray(value)) {
    return value.map(normalizeForHashing)
  }

  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, normalizeForHashing(val)]))
  }

  return value
}

/**
 * Custom hashKey function that serializes URLSearchParams to sorted strings.
 */
function customHashKey(queryKey: QueryKey): string {
  const normalized = normalizeForHashing(queryKey)
  return defaultHashKey(normalized as QueryKey)
}

function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: false,
        networkMode: 'always',
        queryKeyHashFn: customHashKey,
      },
      mutations: {
        networkMode: 'always',
      },
    },
  })
}

let browserQueryClient: QueryClient
export function getQueryClient(): QueryClient {
  return (browserQueryClient ??= createQueryClient())
}
